import { Link, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { CompanyStatusBadge, SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { NavIcon } from '../components/NavIcon.tsx';
import { InfoRow, ReadOnlyValue } from '../components/InfoRows.tsx';
import { EntityQuestionnaireTabs } from '../admin/EntityQuestionnaireTabs.tsx';
import {
  buildEntityQuestionnaireTabs,
  type QuestionnaireGroup,
} from '../lib/entityQuestionnaireTabs.ts';
import type {
  AdminQuestionnaire,
  CompanyDriverSubmission,
  Driver,
  Question,
  QuestionCategory,
} from '../lib/types.ts';

type DriverDetailResponse = {
  driver: Driver;
  questionnaires: AdminQuestionnaire[];
  questions: Question[];
  categories: QuestionCategory[];
  submissions: CompanyDriverSubmission[];
};

export function CompanyDriverDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['me', 'driver', id],
    queryFn: () => companyApi.get<DriverDetailResponse>(`/api/me/drivers/${id}`),
    enabled: !!id,
  });

  const invite = useMutation({
    mutationFn: () => companyApi.post(`/api/me/drivers/${id}/invite`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'driver', id] }),
  });

  const questionnaires = data?.questionnaires ?? [];
  const questions = data?.questions ?? [];
  const categories = data?.categories ?? [];
  const submissions = data?.submissions ?? [];

  const groups = useMemo(
    () => buildEntityQuestionnaireTabs(questionnaires, categories, questions),
    [questionnaires, categories, questions],
  );
  const subByQ = useMemo(() => {
    const m = new Map<string, CompanyDriverSubmission>();
    for (const s of submissions) m.set(s.question_id, s);
    return m;
  }, [submissions]);

  if (isLoading) return <CenteredSpinner />;
  if (error) return <ErrorMessage error={error} />;
  const driver = data?.driver;
  if (!driver) return <ErrorMessage error={new Error('Driver not found')} />;

  // Read-only question row: status badge, question + answer (or file downloads),
  // and — for rejected answers — the reviewer's reason + reference doc. No
  // review buttons: the company cannot change compliance.
  const renderQuestion = (question: Question) => {
    const sub = subByQ.get(question.id);
    return (
      <div className="py-3">
        {sub && (
          <div className="mb-1">
            <SubmissionStatusBadge status={sub.status} />
          </div>
        )}
        <div className="text-sm font-semibold text-slate-900">{question.label}</div>
        {question.help_text && (
          <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
        )}
        <div className="mt-0.5 text-sm text-slate-600">
          {(sub?.submission_files?.length ?? 0) > 0 ? (
            <span className="flex flex-col gap-0.5">
              {sub!.submission_files.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  className="inline-flex items-center gap-1 text-left text-blue-600 hover:underline"
                  onClick={async () => {
                    const res = await companyApi.get<{ url: string }>(
                      `/api/me/drivers/${id}/submissions/${sub!.id}/files/${file.id}/download-url`,
                    );
                    window.open(res.url, '_blank', 'noopener');
                  }}
                >
                  📎 {file.file_name}
                </button>
              ))}
            </span>
          ) : (
            answerPreview(sub)
          )}
        </div>
        {sub?.status === 'rejected' && (sub.reviewer_comment || sub.reference_doc_url) && (
          <div className="mt-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
            {sub.reviewer_comment && <div>{sub.reviewer_comment}</div>}
            {sub.reference_doc_url && (
              <a
                href={sub.reference_doc_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-block font-medium text-orange-900 underline"
              >
                📎 {sub.reference_doc_name ?? 'Reference document'}
              </a>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Link to="/c/drivers" className="text-sm text-blue-600 hover:underline">
        <NavIcon name="chevron-right" size={14} style={{ transform: 'rotate(180deg)' }} /> Back to
        drivers
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-900">{driver.name}</h1>
            <CompanyStatusBadge status={driver.status} />
          </div>
          <div className="mt-1 text-sm text-slate-500">{driver.email}</div>
          {driver.contractor_type && (
            <div className="mt-1 text-xs text-slate-400">{driver.contractor_type}</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {invite.isError && <ErrorMessage error={invite.error} />}
          <Btn variant="ghost" onClick={() => invite.mutate()} loading={invite.isPending}>
            {driver.invited_at ? 'Resend invite' : 'Invite'}
          </Btn>
        </div>
      </div>

      <EntityQuestionnaireTabs
        infoLabel="Driver information"
        infoPanel={<DriverInfoPanel driver={driver} />}
        groups={groups}
        tabBadge={(g) => outstandingCount(g, subByQ)}
        renderQuestion={renderQuestion}
        ariaLabel="Driver questionnaire sections"
      />
    </div>
  );
}

function DriverInfoPanel({ driver }: { driver: Driver }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 md:max-w-xl">
      <h3 className="mb-1 text-base font-semibold text-slate-800">Details</h3>
      <InfoRow label="Name">
        <ReadOnlyValue value={driver.name} />
      </InfoRow>
      <InfoRow label="Email">
        <ReadOnlyValue value={driver.email} />
      </InfoRow>
      <InfoRow label="Phone">
        <ReadOnlyValue value={driver.phone} />
      </InfoRow>
      <InfoRow label="Contractor type">
        <ReadOnlyValue value={driver.contractor_type} />
      </InfoRow>
      <InfoRow label="Invited">
        <ReadOnlyValue
          value={driver.invited_at ? new Date(driver.invited_at).toLocaleString() : null}
        />
      </InfoRow>
      <InfoRow label="Submitted">
        <ReadOnlyValue
          value={driver.submitted_at ? new Date(driver.submitted_at).toLocaleString() : null}
        />
      </InfoRow>
    </div>
  );
}

// Minimal answer preview (mirrors the admin driver page).
function answerPreview(sub: CompanyDriverSubmission | undefined): string {
  if (!sub) return 'Not answered yet';
  const a = sub.answer;
  if (a === null || a === undefined || a === '') return 'Not answered yet';
  if (Array.isArray(a)) return (a as string[]).join(', ');
  if (typeof a === 'boolean') return a ? 'Yes' : 'No';
  if (typeof a === 'object') return 'Answered';
  return String(a);
}

// Tab badge: outstanding items in this questionnaire (pending or rejected),
// matching the admin driver page.
function outstandingCount(
  group: QuestionnaireGroup,
  subByQ: Map<string, CompanyDriverSubmission>,
): number {
  let n = 0;
  for (const section of group.sections) {
    for (const q of section.questions) {
      const sub = subByQ.get(q.id);
      if (sub && (sub.status === 'pending' || sub.status === 'rejected')) n += 1;
    }
  }
  return n;
}
